import React, { useState } from "react";
import axios from "axios";

interface PasswordGateProps {
  children: React.ReactNode;
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    width: "100%",
  },
  passwordGate: {
    backgroundColor: "#f5f5f5",
    padding: "2rem",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    textAlign: "center" as const,
    marginBottom: "50vh",
    marginTop: "6vh",
  },
  heading: {
    marginBottom: "1rem",
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "1rem",
  },
  input: {
    padding: "0.5rem",
    fontSize: "16px",
  },
  button: {
    padding: "0.5rem",
    fontSize: "1rem",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  error: {
    color: "red",
    marginTop: "1rem",
  },
};

// Which flags file this deploy reads: Netlify env REACT_APP_CHANNEL=release
// selects flags-release.json; default is the beta flags.
export const FLAGS_FILE =
  process.env.REACT_APP_CHANNEL === "release" ? "levels/flags-release.json" : "levels/flags.json";

const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  // Release channel: no gate, decided before the first paint (the form
  // flashed for a frame on attackofthespheres.com, Joshua 2026-09-07)
  const release = process.env.REACT_APP_CHANNEL === "release";
  const [isAuthenticated, setIsAuthenticated] = useState(release);
  // Until the channel's flags file answers, paint nothing rather than the form
  const [flagsKnown, setFlagsKnown] = useState(release);
  // The gate itself is a feature flag: public launch = flip passwordGate
  // to false in the channel's flags file (2026-09-02)
  React.useEffect(() => {
    if (release) return;
    fetch(FLAGS_FILE)
      .then((r) => (r.ok ? r.json() : null))
      .then((f) => { if (f && f.passwordGate === false) setIsAuthenticated(true); })
      .catch(() => {})
      .finally(() => setFlagsKnown(true));
  }, [release]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // In development, check password directly against environment variable
    if (process.env.NODE_ENV === 'development') {
      const correctPassword = process.env.REACT_APP_BETA_PASSWORD;
      if (password === correctPassword) {
        setIsAuthenticated(true);
      } else {
        setError("Incorrect password");
      }
      return;
    }

    // In production, use Netlify function
    try {
      const response = await axios.post("/api/verify-password", { password });
      if (response.data.success) {
        setIsAuthenticated(true);
      } else {
        setError("Incorrect password");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    }
  };

  if (!flagsKnown) return <div style={{ position: "fixed", inset: 0, background: "#0b1020" }} />;
  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.passwordGate}>
        <h2 style={styles.heading}>Beta Access</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter beta password"
            style={styles.input}
          />
          <button type="submit" style={styles.button}>
            Access Beta
          </button>
        </form>
        {error && <p style={styles.error}>{error}</p>}
      </div>
    </div>
  );
};

export default PasswordGate;
